import { create } from 'zustand';
import type { Locale, LoginRequest, RegisterRequest, UserDto } from '@indusketch/shared';
import { authApi } from '../../lib/api/endpoints.js';
import { ApiError } from '../../lib/api/client.js';
import { useLocaleStore } from '../../i18n/i18n.js';

type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: UserDto | null;
  init: () => Promise<void>;
  login: (input: LoginRequest) => Promise<void>;
  /** 'pending': the account waits for an admin's approval and nobody is signed in yet. */
  register: (input: RegisterRequest) => Promise<'signed-in' | 'pending'>;
  logout: () => Promise<void>;
  /** Interface language: applies at once and is stored in the account when signed in. */
  setLanguage: (locale: Locale) => Promise<void>;
}

/** A language stored in the account wins over the device's choice. */
function adopt(user: UserDto) {
  if (user.locale) useLocaleStore.getState().setLocale(user.locale);
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  status: 'unknown',
  user: null,
  async init() {
    try {
      const { user } = await authApi.me();
      adopt(user);
      set({ status: 'authenticated', user });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) set({ status: 'anonymous', user: null });
      else throw err;
    }
  },
  async login(input) {
    const { user } = await authApi.login(input);
    adopt(user);
    set({ status: 'authenticated', user });
  },
  async register(input) {
    const res = await authApi.register({ ...input, locale: useLocaleStore.getState().locale });
    if ('pending' in res) return 'pending';
    set({ status: 'authenticated', user: res.user });
    return 'signed-in';
  },
  async logout() {
    await authApi.logout();
    set({ status: 'anonymous', user: null });
  },
  async setLanguage(locale) {
    useLocaleStore.getState().setLocale(locale);
    if (get().status !== 'authenticated') return;
    const { user } = await authApi.updateMe({ locale });
    set({ user });
  },
}));
